import { useState } from "react";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Eye, Code, Type } from "lucide-react";
import { preserveRichText, cleanPastedContent } from "@/utils/textFormatter";

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  rows?: number;
}

export const RichTextEditor = ({ value, onChange, placeholder, rows = 15 }: RichTextEditorProps) => {
  const [mode, setMode] = useState<'split' | 'editor' | 'preview'>('split');

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    
    // Récupérer le contenu collé (HTML si disponible, sinon texte)
    const html = e.clipboardData.getData('text/html');
    const text = e.clipboardData.getData('text/plain');
    
    let content = '';
    if (html && html.trim()) {
      // Préserver le HTML en nettoyant seulement les éléments dangereux
      content = html
        // Garde tous les éléments de formatage
        .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '')
        .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
        .replace(/on\w+="[^"]*"/gi, '')
        // Préserve les espaces et formatage
        .replace(/\s{2,}/g, (match) => '&nbsp;'.repeat(match.length))
        // Convertit les divs en paragraphes
        .replace(/<div[^>]*>/gi, '<p>')
        .replace(/<\/div>/gi, '</p>')
        // Nettoie les attributs inutiles mais garde le formatage
        .replace(/<(\w+)[^>]*>/gi, (match, tag) => {
          if (['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'strong', 'b', 'em', 'i', 'u', 'ul', 'ol', 'li', 'br'].includes(tag.toLowerCase())) {
            return `<${tag.toLowerCase()}>`;
          }
          return match;
        });
    } else if (text) {
      // Pour le texte brut, préserver les retours à la ligne et espaces
      content = text
        .replace(/\r?\n/g, '<br>')
        .replace(/  +/g, (match) => '&nbsp;'.repeat(match.length));
    }
    
    // Insérer le contenu à la position du curseur
    const textarea = e.target as HTMLTextAreaElement;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const before = value.substring(0, start);
    const after = value.substring(end);
    
    const newValue = before + content + after;
    onChange(newValue);
    
    // Remettre le curseur à la bonne position
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + content.length, start + content.length);
    }, 0);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium">Contenu de l'article *</Label>
        <div className="flex gap-2">
          <Button
            type="button"
            variant={mode === 'split' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setMode('split')}
          >
            <Type className="w-3 h-3 mr-1" />
            <Eye className="w-3 h-3" />
            Split
          </Button>
          <Button
            type="button"
            variant={mode === 'editor' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setMode('editor')}
          >
            <Type className="w-3 h-3 mr-1" />
            Édition
          </Button>
          <Button
            type="button"
            variant={mode === 'preview' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setMode('preview')}
          >
            <Eye className="w-3 h-3 mr-1" />
            Aperçu
          </Button>
        </div>
      </div>

      {mode === 'split' ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Zone d'édition */}
          <div className="space-y-2">
            <Label className="text-xs font-medium text-muted-foreground">✏️ Éditeur</Label>
            <Textarea
              placeholder={placeholder || `Collez votre contenu ici... Le formatage sera préservé !

✨ Conseils :
• Copiez-collez directement votre texte formaté
• Les titres, gras, italique seront gardés
• Les émojis et icônes sont préservés
• Les listes à puces sont maintenues
• HTML accepté pour un contrôle total`}
              rows={rows}
              value={value}
              onChange={(e) => onChange(e.target.value)}
              onPaste={handlePaste}
              className="font-mono text-sm resize-y min-h-[400px]"
            />
          </div>
          
          {/* Aperçu en temps réel */}
          <div className="space-y-2">
            <Label className="text-xs font-medium text-muted-foreground">👁️ Aperçu en temps réel</Label>
            <div className="border rounded-md p-4 min-h-[400px] bg-white overflow-y-auto">
              <div 
                className="prose prose-sm max-w-none 
                  prose-headings:font-display prose-headings:font-bold prose-headings:text-primary
                  prose-h2:text-xl prose-h2:mt-6 prose-h2:mb-4 prose-h2:border-b prose-h2:border-border prose-h2:pb-2
                  prose-h3:text-lg prose-h3:mt-4 prose-h3:mb-3 prose-h3:text-secondary
                  prose-h4:text-base prose-h4:mt-3 prose-h4:mb-2 prose-h4:text-primary prose-h4:font-semibold
                  prose-p:mb-4 prose-p:leading-relaxed
                  prose-ul:mb-4 prose-ol:mb-4 prose-li:mb-1
                  prose-strong:text-primary prose-strong:font-semibold
                  prose-em:text-secondary prose-em:italic"
                dangerouslySetInnerHTML={{ 
                  __html: preserveRichText(value) || '<p class="text-muted-foreground italic">Votre contenu apparaîtra ici en temps réel...</p>' 
                }}
              />
            </div>
          </div>
        </div>
      ) : mode === 'editor' ? (
        <div>
          <Textarea
            placeholder={placeholder || `Collez votre contenu ici... Le formatage sera préservé !

✨ Conseils :
• Copiez-collez directement votre texte formaté
• Les titres, gras, italique seront gardés
• Les émojis et icônes sont préservés
• Les listes à puces sont maintenues
• HTML accepté pour un contrôle total`}
            rows={rows}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onPaste={handlePaste}
            className="font-mono text-sm resize-y min-h-[400px]"
          />
        </div>
      ) : (
        <div className="border rounded-md p-4 min-h-[400px] bg-white">
          <div 
            className="prose prose-lg max-w-none 
              prose-headings:font-display prose-headings:font-bold prose-headings:text-primary
              prose-h2:text-2xl prose-h2:mt-8 prose-h2:mb-6 prose-h2:border-b prose-h2:border-border prose-h2:pb-2
              prose-h3:text-xl prose-h3:mt-6 prose-h3:mb-4 prose-h3:text-secondary
              prose-h4:text-lg prose-h4:mt-4 prose-h4:mb-3 prose-h4:text-primary prose-h4:font-semibold
              prose-p:mb-6 prose-p:leading-relaxed prose-p:text-lg
              prose-ul:mb-6 prose-ol:mb-6 prose-li:mb-2
              prose-strong:text-primary prose-strong:font-semibold
              prose-em:text-secondary prose-em:italic"
            dangerouslySetInnerHTML={{ 
              __html: preserveRichText(value) || '<p class="text-muted-foreground italic">Votre contenu apparaîtra ici...</p>' 
            }}
          />
        </div>
      )}
      
      <div className="flex justify-between items-center text-xs text-muted-foreground">
        <span>
          💡 Astuce : Copiez-collez directement votre contenu formaté depuis Word, Google Docs ou n'importe quel éditeur !
        </span>
        <span>
          Environ {Math.ceil(value.split(' ').filter(word => word.length > 0).length / 200)} min de lecture
          • {value.split(' ').filter(word => word.length > 0).length} mots
        </span>
      </div>
    </div>
  );
};